import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowDownIcon } from 'lucide-react';
import { heroSlides } from '../../data/homepage';
import { PillButton } from '../ui/PillButton';
import { HeroAtna } from '../HeroAtna';
const AUTOPLAY_MS = 7000;

/**
 * Grande illustration du hero, titre en capitales posé dessus, ATNA en panneau flottant.
 *
 * Le bloc de contenu est en flux normal avec `mt-auto` plutôt qu'en `absolute bottom-0` :
 * avec ATNA dans la pile, un titre sur deux lignes débordait d'une hauteur figée. Ici le
 * conteneur grandit si la pile grandit, et `min-h` ne sert que de plancher.
 *
 * ATNA est posé HORS du bloc animé par `AnimatePresence` : intégré dedans, il serait
 * remonté à chaque changement de diapositive et effacerait ce que le citoyen est en
 * train d'écrire.
 */
export function HeroSlideshow() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const go = useCallback((next: number) => {
    setIndex((next + heroSlides.length) % heroSlides.length);
  }, []);
  useEffect(() => {
    if (paused) return;
    // Respecte le réglage système : pas de défilement automatique si l'animation est réduite.
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const id = window.setInterval(() => setIndex(i => (i + 1) % heroSlides.length), AUTOPLAY_MS);
    return () => window.clearInterval(id);
  }, [paused]);
  const slide = heroSlides[index];
  const scrollDown = () => {
    const bottom = (containerRef.current?.getBoundingClientRect().bottom ?? 0) + window.scrollY;
    window.scrollTo({
      top: bottom,
      behavior: 'smooth'
    });
  };
  return <div ref={containerRef} role="region" aria-roledescription="carrousel" aria-label="À la une" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocusCapture={() => setPaused(true)} onBlurCapture={() => setPaused(false)} className="relative isolate flex min-h-[32rem] w-full flex-col overflow-hidden bg-ville-sand sm:min-h-[36rem]" data-id="element-1775" data-mp="3uzawn3jg0kai">
      <AnimatePresence initial={false} data-id="element-1776" data-mp="1qnmsdgec2y5z">
        <motion.img key={slide.id} src={slide.image} alt="" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.45,
        ease: [0.23, 1, 0.32, 1]
      }} className="absolute inset-0 z-0 h-full w-full object-cover" data-id="element-1777" data-mp="1qmlcmymdhnhx" />
      </AnimatePresence>

      {/* Dégradé plutôt qu'une ombre portée sur le texte : le titre reste lisible quelle que
          soit l'illustration, sans salir la typographie. Renforcé en bas depuis qu'ATNA y
          pose un panneau blanc, pour que son bord se détache de l'image. */}
      <div className="absolute inset-0 z-10 bg-gradient-to-t from-black/75 via-black/25 to-transparent" aria-hidden="true" data-id="element-1778" data-mp="320ywlse09iso" />

      <div className="relative z-20 mt-auto p-6 sm:p-10 lg:p-12" data-id="element-1779" data-mp="34o9dpmy2bfc1">
        <AnimatePresence mode="wait" data-id="element-1780" data-mp="39mkmrvgj9lt7">
          <motion.div key={slide.id} initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} transition={{
          duration: 0.3,
          ease: [0.23, 1, 0.32, 1]
        }} data-id="element-1781" data-mp="39lj1ky5szixr">
            <h1 className="max-w-3xl font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tight text-white sm:text-5xl lg:text-6xl" data-id="element-1782" data-mp="2lolpv6c63dak">
              {slide.title}
            </h1>
            <PillButton to={slide.to} variant="orange" className="mt-6" data-id="element-1783" data-mp="2vmt3bf78zd7t">
              {slide.cta}
            </PillButton>
          </motion.div>
        </AnimatePresence>

        <div className="mt-7 max-w-2xl" data-id="element-1784" data-mp="2zppsjoram7kq">
          <HeroAtna data-id="element-1785" data-mp="awu7g43h8ulk" />
        </div>

        <div className="mt-7 flex items-center gap-4" data-id="element-1786" data-mp="avsnfa4hgxrp">
          <p className="shrink-0 text-sm font-bold tabular-nums text-white" aria-live="polite" data-id="element-1787" data-mp="exvzo0yz86e1">
            {index + 1}/{heroSlides.length}
          </p>
          <ul className="flex flex-1 items-center gap-2" data-id="element-1788" data-mp="dhig7wstk7ln">
            {heroSlides.map((item, i) => <li key={item.id} className="min-w-0 flex-1" data-id="element-1789" data-mp="8jqeyc6ab94s" data-mpi={i}>
                <button type="button" onClick={() => go(i)} aria-label={`Diapositive ${i + 1} : ${item.title}`} aria-current={i === index} className={`h-0.5 w-full rounded-full transition-colors duration-200 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-white ${i === index ? 'bg-white' : 'bg-white/40 hover:bg-white/70'}`} data-id="element-1790" data-mp="nfg58mii68fb" />
              </li>)}
          </ul>
          <button type="button" onClick={scrollDown} aria-label="Faire défiler vers le contenu" className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-white transition-colors duration-150 ease-ville hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-1791" data-mp="117crwb1ejq4b">
            <ArrowDownIcon className="h-5 w-5" aria-hidden="true" data-id="element-1792" data-mp="1yse2w5a1fp9g" />
          </button>
        </div>
      </div>
    </div>;
}